/**
 * studentAccountAdapter — le compte connecté (`currentUser.object`) → ce que « Mon compte » montre.
 *
 * Port de l'écran legacy `student/account/MyAccountScreen.js` et de ses pièces, chacune citée là où
 * sa règle est reprise : `ProfileInfo` (classe, matières, ancienneté, fuseau), `AccountProgression`
 * (la quête la plus avancée, le rang, les gestes), `ChooseTitleModal` + `SkillSetTitleButton` (une
 * option de titre par quête), `ActivitiesStatistics` (les deux périodes), `ValidateEmailActionCard`
 * et `CompleteProfileCard` (le bloc « À faire »).
 *
 * Les formes `Api*` sont lâches : le compte vient d'un réducteur en JS, tout est facultatif.
 */
import moment from "moment"

import { cleanMediaUrl } from "../../../../../data/constants/apiConstants"
import { weekDayNums, weekDaysObject } from "../../../../../data/constants/activitiesConstants"
import { championSlugsToNames } from "../../../../../data/constants/championsConstants"
// Type encore déclaré dans le design system seul (`ActivityStatsCard`) : lu ici par son seul type.
import { type UIActivityStatsPeriod } from "../../../../../design-system/organisms/ActivityStatsCard"
import {
    type UIStudentAccountGamification,
    type UIStudentAccountIdentity,
    type UIStudentTitleOption,
    type UIStudentTitleProgress,
} from "../../../ui/types/studentAccount"
import { toUISubject, type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"
import { formatFrenchDe, mostAdvancedSkillSet, rankLabel } from "../shared/questAdapter"
import { canReadSubject } from "../shared/studentOfferAdapter"

export type ApiSkillSetStep = {
    title_gained?: string | null
    skill_id?: number | string
    skill_level?: number | string
}

export type ApiUserSkillSet = {
    id?: number | string
    champion?: string
    current_step?: number
    current_count?: number
    steps?: ApiSkillSetStep[]
}

/** Une compétence des données statiques (`staticData.skills`) : le total à atteindre par niveau. */
export type ApiSkill = {
    id?: number | string
    levels_totals?: Record<string, number> | number[]
}

type ApiPeriodStatistics = {
    activities?: { count?: number; duration?: number; average_score?: number | string | null }
    activity_days?: [number?, number?]
}

export type ApiStudentStatistics = {
    end_of_week_day?: string
    today?: ApiPeriodStatistics
    this_week?: ApiPeriodStatistics
    last_week?: ApiPeriodStatistics
}

export type ApiStudentAccountUser = {
    id?: number | string
    display_name?: string
    avatar?: string | null
    roles?: string[]
    created_at?: string
    timezone?: string | null
    is_verified?: boolean
    profile_completion?: number
    profile?: { class?: string | null } | null
    prioritized_school_subjects?: ApiSchoolSubject[] | null
    student_title?: string | null
    title_skill_set_id?: number | string | null
    xp?: number
    petals?: number
    rank?: number | null
    shop_purchases?: Array<{ shop_product_id?: number }> | null
    skill_sets?: ApiUserSkillSet[] | null
    statistics?: ApiStudentStatistics | null
}

export const hasRole = (user: { roles?: string[] } | null | undefined, role: string): boolean =>
    user?.roles?.includes(role) ?? false

const championName = (skillSet: ApiUserSkillSet): string =>
    (championSlugsToNames as Record<string, string>)[skillSet.champion ?? ""] ?? ""

/**
 * « 3 arbres · 1 repas » : les achats de la boutique, comptés comme `AccountProgression` (produit 1,
 * les arbres ; produit 2, les repas). Aucun geste : rien.
 */
export function gesturesLabel(purchases: ApiStudentAccountUser["shop_purchases"]): string | null {
    const trees = purchases?.filter((purchase) => purchase?.shop_product_id === 1).length ?? 0
    const meals = purchases?.filter((purchase) => purchase?.shop_product_id === 2).length ?? 0
    if (trees === 0 && meals === 0) return null
    return `${trees} ${trees > 1 ? "arbres" : "arbre"} · ${meals} repas`
}

/** La ligne « Quêtes » : `AccountProgression` variante `quest` — `n/N` avec `N` = toutes les étapes. */
export function questOf(skillSet: ApiUserSkillSet | null): UIStudentAccountGamification["quest"] {
    if (skillSet == null) return null
    const total = skillSet.steps?.length ?? 0
    const step = skillSet.current_step ?? 0
    return {
        label: `Quête ${formatFrenchDe(championName(skillSet))} ${step}/${total}`,
        progress: total > 0 ? Math.round((step / total) * 100) : 0,
    }
}

/** Le bloc de jeu : seulement pour un élève (l'ancien écran le posait sous `hasRole(user, "student")`). */
export function toStudentAccountGamification(user: ApiStudentAccountUser | null | undefined) {
    if (!user || !hasRole(user, "student")) return null
    const gamification: UIStudentAccountGamification = {
        title: user.student_title ?? null,
        xp: user.xp ?? 0,
        petals: user.petals ?? 0,
        quest: questOf(mostAdvancedSkillSet(user.skill_sets)),
        rankLabel: rankLabel(user.rank),
        gesturesLabel: gesturesLabel(user.shop_purchases),
    }
    return gamification
}

/**
 * Qui est l'élève — `ProfileInfo` (`showSchoolSubjects` sous `my_successes`), le fuseau seulement
 * s'il diffère de celui du serveur, l'ancienneté au format `MMM YYYY`.
 */
export function toStudentAccountIdentity(
    user: ApiStudentAccountUser | null | undefined,
    serverTimezone: string | null | undefined
): UIStudentAccountIdentity | null {
    if (!user) return null
    const timezone = user.timezone ?? null
    const showSubjects = canReadSubject(user?.roles, "my_successes")
    return {
        displayName: user.display_name ?? "",
        avatarUrl: user.avatar ? cleanMediaUrl(user.avatar) : null,
        className: user.profile?.class ?? null,
        subjects: showSubjects ? (user.prioritized_school_subjects ?? []).map(toUISubject) : [],
        memberSince: moment(user.created_at).format("MMM YYYY"),
        timezone: timezone && serverTimezone && timezone !== serverTimezone ? timezone : null,
    }
}

/**
 * Une option par quête — `ChooseTitleModal` + `SkillSetTitleButton` : le titre de l'étape en cours
 * et « Quêtes de Zoé finies : n/N » (`N` = étapes − 1). L'ancien écran n'en montrait aucune tant que
 * les compétences statiques n'étaient pas là.
 */
export function toStudentTitleOptions(
    skillSets: ApiUserSkillSet[] | null | undefined,
    skills: ApiSkill[] | null | undefined
): UIStudentTitleOption[] {
    if (!skills || !skillSets) return []
    return skillSets
        .map((skillSet): UIStudentTitleOption | null => {
            if (skillSet?.id == null) return null
            const step = skillSet.current_step ?? 0
            const title = skillSet.steps?.[step - 1]?.title_gained
            if (!title) return null
            return {
                id: skillSet.id,
                title,
                detail: `Quêtes ${formatFrenchDe(championName(skillSet))} finies : ${step - 1}/${(skillSet.steps?.length ?? 0) - 1}`,
            }
        })
        .filter((option): option is UIStudentTitleOption => option != null)
}

/** Le total d'une étape : `skill.levels_totals[step.skill_level]`, comme `SkillItem.js`. */
function stepTotal(step: ApiSkillSetStep | undefined, skills: ApiSkill[] | null | undefined): number | null {
    if (!step) return null
    const skill = skills?.find((candidate) => String(candidate?.id) === String(step.skill_id))
    const totals = skill?.levels_totals as Record<string, number> | undefined
    const total = totals?.[String(step.skill_level)]
    return typeof total === "number" && total > 0 ? total : null
}

/**
 * Le titre porté et le chemin vers le suivant, dans la quête du titre (`title_skill_set_id`, 1 par
 * défaut comme le serveur) — la règle de `DECISIONS.md` (13/10, `<TitleProgress>`) : les étapes finies
 * depuis le titre, plus l'avancement de l'étape en cours (`current_count` sur son total), sur les
 * étapes jusqu'au titre suivant. Au dernier titre de la quête : pas de suivant, l'anneau est plein.
 */
export function toStudentTitleProgress(
    user: ApiStudentAccountUser | null | undefined,
    skills: ApiSkill[] | null | undefined
): UIStudentTitleProgress | null {
    if (!user || !hasRole(user, "student")) return null
    const titleSkillSetId = user.title_skill_set_id ?? 1
    const skillSet = user.skill_sets?.find((candidate) => String(candidate?.id) === String(titleSkillSetId))
    const steps = skillSet?.steps ?? []
    const index = (skillSet?.current_step ?? 0) - 1
    const title = user.student_title ?? steps[index]?.title_gained
    if (!skillSet || index < 0 || index >= steps.length || !title) return null

    const held = steps[index]?.title_gained
    let since = index
    while (since > 0 && steps[since - 1]?.title_gained === held) since -= 1
    let next = index + 1
    while (next < steps.length && steps[next]?.title_gained === held) next += 1
    if (next >= steps.length) return { title, nextTitle: null, progress: 100 }

    const total = stepTotal(steps[index], skills)
    const inStep = total != null ? Math.min(1, Math.max(0, (skillSet.current_count ?? 0) / total)) : 0
    const progress = ((index - since + inStep) / (next - since)) * 100
    return { title, nextTitle: steps[next]?.title_gained ?? null, progress: Math.round(progress) }
}

const plural = (word: string, count: number | null | undefined) => (count != null && count > 1 ? `${word}s` : word)

/** `niceTimeFromSeconds` (`data/utility/Utils.js`) : la valeur, et son unité. */
function niceTime(seconds: number | null | undefined): [string, string] {
    const duration = seconds ?? 0
    if (duration < 3600) {
        const minutes = Math.floor(duration / 60)
        return [String(minutes), plural("minute", minutes)]
    }
    const hours = Math.floor(duration / 3600)
    const minutes = Math.floor((duration - hours * 3600) / 60)
    return [`${hours}h${minutes > 0 ? minutes : ""}`, "temps"]
}

/** Le score moyen, au format français : « 4,5 », ou « - » sans score. */
function scoreLabel(score: number | string | null | undefined): string {
    if (score == null || score === "") return "-"
    return String(score).replace(".", ",")
}

function periodTiles(period: ApiPeriodStatistics | undefined) {
    const [durationValue, durationUnit] = niceTime(period?.activities?.duration)
    const count = period?.activities?.count
    const days = period?.activity_days?.[0]
    return [
        { key: "duration", value: durationValue, label: `${durationUnit} d'activité` },
        {
            key: "activities",
            value: String(count ?? 0),
            label: `${plural("activité", count)} ${plural("réalisée", count)}`,
        },
        {
            key: "days",
            value: String(days ?? 0),
            label: `${plural("jour", days)} d'activité sur ${period?.activity_days?.[1] ?? 0}`,
        },
        { key: "score", value: scoreLabel(period?.activities?.average_score), label: "score qualité" },
    ]
}

/**
 * « Mes statistiques » — port d'`ActivitiesStatistics.js` : « Aujourd'hui », et la semaine depuis
 * son premier jour (le lendemain d'`end_of_week_day`) — « Depuis hier » au lendemain de ce jour,
 * « La semaine dernière » le jour même. L'ancien bouton alternait ; les deux périodes se lisent ici
 * en pilules, celle qu'il ouvrait d'abord est `initialPeriodKey`. `now` : l'heure lue par l'appelant.
 */
export function toStudentActivityStats(
    statistics: ApiStudentStatistics | null | undefined,
    now: moment.Moment
): { periods: UIActivityStatsPeriod[]; initialPeriodKey: string } | null {
    if (!statistics) return null
    const endDay = statistics.end_of_week_day ?? ""
    const endDayName = endDay.charAt(0).toUpperCase() + endDay.slice(1)
    const endIndex = (weekDayNums as Record<string, number>)[endDayName]
    const startOfWeekDay = ((endIndex ?? Number.NaN) + 1) % 7
    // `moment().weekday()` de l'ancien code, en locale française (lundi = 0) : lu ici sans dépendre
    // de la locale chargée.
    const diff = ((now.isoWeekday() + 6) % 7) - startOfWeekDay
    const startName = Object.values(weekDaysObject as Record<string, string>)[startOfWeekDay] ?? "Lundi"
    const weekKey = diff === 0 ? "last_week" : "this_week"
    // Les mots de l'ancien bouton, en minuscules comme la maquette (« Depuis lundi dernier »).
    const weekLabel =
        diff === 0 ? "La semaine dernière" : `Depuis ${diff === 1 ? "hier" : `${startName.toLowerCase()} dernier`}`

    return {
        periods: [
            { key: "today", label: "Aujourd'hui", tiles: periodTiles(statistics.today) },
            { key: weekKey, label: weekLabel, tiles: periodTiles(statistics[weekKey]) },
        ],
        initialPeriodKey: diff === 0 ? "today" : weekKey,
    }
}

/** Les formules qui montrent « Complète ton profil » — `CompleteProfileCard.js` (sans `plus`). */
const PROFILE_TODO_PRODUCTS = ["premium-student", "initial-student", "essential-student", "integral-student"]

/** Le bloc « À faire » : l'email à valider, le profil à compléter. */
export function toStudentAccountTodos(user: ApiStudentAccountUser | null | undefined) {
    const completion = user?.profile_completion
    return {
        needsEmailValidation: user != null && !user.is_verified,
        canCompleteProfile:
            completion != null && completion < 100 && PROFILE_TODO_PRODUCTS.some((role) => hasRole(user, role)),
        profileCompletion: completion ?? null,
    }
}
